// ===========================================
// hooks/useAppState.js
// ===========================================
import { useEffect, useMemo, useCallback, useReducer } from 'react';
import Fuse from 'fuse.js';
import { api } from '../utils/api';
import config from '../utils/config';

const defaultState = {
  user: null,
  users: [],
  books: [],
  filteredBooks: [],
  pendingUsers: [],
  sortBy: 'relevance',
  isLoading: true,
  isError: false,
  isBooksLoading: true,
  isUsersLoading: true,
  booksError: false,
  usersError: false,
};

const reducer = (state, action) => {
  switch (action.type) {
    case "SET_LOADING":
      return { ...state, isLoading: action.payload };
    
    case "SET_BOOKS_LOADING":
      return { ...state, isBooksLoading: action.payload };
    
    case "SET_USERS_LOADING":
      return { ...state, isUsersLoading: action.payload };
    
    case "SET_ERROR":
      return { ...state, isError: action.payload, isLoading: false };
    
    case "SET_BOOKS_ERROR":
      return { ...state, booksError: action.payload, isBooksLoading: false };
    
    case "SET_USERS_ERROR":
      return { ...state, usersError: action.payload, isUsersLoading: false };
    
    case "FETCH_BOOKS_SUCCESS":
      return { 
        ...state, 
        books: action.payload, 
        filteredBooks: action.payload,
        isBooksLoading: false,
        booksError: false 
      };
    
    case "FETCH_USERS_SUCCESS":
      return {
        ...state,
        pendingUsers: action.payload,
        isUsersLoading: false,
        usersError: false
      };
    
    case "SET_FILTERED_BOOKS":
      return { ...state, filteredBooks: action.payload };
    
    case "SET_SORT_BY":
      return { ...state, sortBy: action.payload };
    
    case "SET_USER":
      return { ...state, user: action.payload };
    
    case "SET_PENDING_USERS":
      return { ...state, pendingUsers: action.payload };
    
    case "ADD_BOOK":
      const newBooks = [...state.books, action.payload];
      return { 
        ...state, 
        books: newBooks,
        filteredBooks: newBooks 
      };
    
    case "UPDATE_BOOK_DOWNLOADS":
      const updatedBooks = state.books.map(book =>
        book.id === action.payload
          ? { ...book, download_count: (book.download_count || 0) + 1 }
          : book
      );
      return { 
        ...state, 
        books: updatedBooks,
        filteredBooks: state.filteredBooks.map(book =>
          book.id === action.payload
            ? { ...book, download_count: (book.download_count || 0) + 1 }
            : book
        )
      };
    
    case "APPROVE_USER":
      return {
        ...state,
        pendingUsers: state.pendingUsers.filter(u => u.id !== action.payload)
      };
    
    case "REJECT_USER":
      return {
        ...state,
        pendingUsers: state.pendingUsers.filter(u => u.id !== action.payload)
      };
    
    case "SET_OVERALL_LOADING":
      return {
        ...state,
        isLoading: action.payload
      };
    
    default:
      return state;
  }
};

export const useAppState = () => {
  const [state, dispatch] = useReducer(reducer, defaultState);
  const { 
    user, 
    books, 
    filteredBooks, 
    pendingUsers, 
    sortBy, 
    isLoading, 
    isError,
    isBooksLoading,
    isUsersLoading,
    booksError,
    usersError
  } = state;

  // Fuse.js configuration
  const fuseOptions = useMemo(() => ({
    keys: [
      { name: 'title', weight: 0.7 },
      { name: 'author', weight: 0.3 },
      { name: 'description', weight: 0.2 }
    ],
    threshold: 0.4,
    includeScore: true,
    includeMatches: true,
    minMatchCharLength: 2
  }), []);

  const fuse = useMemo(() => new Fuse(books, fuseOptions), [books, fuseOptions]);

  // Initialize app - check auth and fetch data
  useEffect(() => {
    const initializeApp = async () => {
      try {
        // Check if user is already authenticated
        const token = localStorage.getItem('access_token');
        
        if (token) {
          try {
            // Verify token and get user profile
            const profileResponse = await api.getProfile();
            dispatch({ type: "SET_USER", payload: profileResponse.user });
            
            // If authenticated, fetch data
            await Promise.allSettled([fetchBooks(), fetchUsers()]);
          } catch (error) {
            console.error('Token verification failed:', error);
            // Token is invalid, clear it
            localStorage.removeItem('access_token');
            dispatch({ type: "SET_USER", payload: null });
          }
        }
        
        // Set overall loading to false
        dispatch({ type: "SET_OVERALL_LOADING", payload: false });
      } catch (error) {
        console.error('Error initializing app:', error);
        dispatch({ type: "SET_OVERALL_LOADING", payload: false });
      }
    };

    const fetchBooks = async () => {
      try {
        dispatch({ type: "SET_BOOKS_LOADING", payload: true });
        const response = await api.getBooks();
        dispatch({ type: "FETCH_BOOKS_SUCCESS", payload: response.books || [] });
      } catch (error) {
        console.error('Error fetching books:', error);
        dispatch({ type: "SET_BOOKS_ERROR", payload: true });
      }
    };

    const fetchUsers = async () => {
      try {
        dispatch({ type: "SET_USERS_LOADING", payload: true });
        
        try {
          const pendingUsersResponse = await api.getPendingUsers();
          dispatch({ type: "FETCH_USERS_SUCCESS", payload: pendingUsersResponse.users || [] });
        } catch (adminError) {
          // User might not be admin, that's ok
          dispatch({ type: "FETCH_USERS_SUCCESS", payload: [] });
        }
      } catch (error) {
        console.error('Error fetching users:', error);
        dispatch({ type: "SET_USERS_ERROR", payload: true });
      }
    };

    initializeApp();
  }, []);

  // Action creators
  const setFilteredBooks = useCallback((books) => {
    dispatch({ type: "SET_FILTERED_BOOKS", payload: books });
  }, []);

  const setSortBy = useCallback((sortBy) => {
    dispatch({ type: "SET_SORT_BY", payload: sortBy });
  }, []);

  const login = useCallback(async (token, userData) => {
    // Set loading state for login process
    dispatch({ type: "SET_OVERALL_LOADING", payload: true });
    
    // Store the JWT token
    localStorage.setItem('access_token', token);
    
    dispatch({ 
      type: "SET_USER", 
      payload: userData
    });
    
    try {
      // Fetch data after successful login
      dispatch({ type: "SET_BOOKS_LOADING", payload: true });
      const response = await api.getBooks();
      dispatch({ type: "FETCH_BOOKS_SUCCESS", payload: response.books || [] });

      // Fetch users if the user is an admin
      if (userData.is_admin) {
        try {
          dispatch({ type: "SET_USERS_LOADING", payload: true });
          const pendingUsersResponse = await api.getPendingUsers();
          dispatch({ type: "FETCH_USERS_SUCCESS", payload: pendingUsersResponse.users || [] });
        } catch (error) {
          console.error('Error fetching users after login:', error);
          dispatch({ type: "SET_USERS_ERROR", payload: true });
        }
      }
    } catch (error) {
      console.error('Error fetching books after login:', error);
      dispatch({ type: "SET_BOOKS_ERROR", payload: true });
    } finally {
      // Always set overall loading to false when login process is complete
      dispatch({ type: "SET_OVERALL_LOADING", payload: false });
    }
  }, []);

  const logout = useCallback(() => {
    // Clear the JWT token
    localStorage.removeItem('access_token');
    dispatch({ type: "SET_USER", payload: null });
  }, []);

  const addBook = useCallback(async (bookData) => {
    try {
      // Upload book via API
      const response = await api.uploadBook(bookData);
      
      // Add the new book to the state
      const newBook = {
        ...response.book,
        id: response.book.id,
        created_at: response.book.upload_date,
        download_count: 0
      };
      dispatch({ type: "ADD_BOOK", payload: newBook });
      
      return response;
    } catch (error) {
      console.error('Error uploading book:', error);
      throw error;
    }
  }, []);

  const updateBookDownloads = useCallback(async (bookId) => {
    try {
      // Download the book via API which should increment download count
      const response = await api.downloadBook(bookId);
      
      // Update the download count in local state
      dispatch({ type: "UPDATE_BOOK_DOWNLOADS", payload: bookId });
      
      // Trigger actual file download
      if (response.download_url) {
        const downloadUrl = response.download_url.startsWith('http') 
          ? response.download_url 
          : `${config.getApiUrl().replace('/api', '')}${response.download_url}`;
        
        // Create a temporary download link
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = '';
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Error downloading book:', error);
      throw error;
    }
  }, []);

  const approveUser = useCallback(async (userId) => {
    try {
      // Approve user via API
      await api.approveUser(userId);
      
      // Remove from pending users in state
      dispatch({ type: "APPROVE_USER", payload: userId });
    } catch (error) {
      console.error('Error approving user:', error);
      throw error;
    }
  }, []);

  const rejectUser = useCallback(async (userId) => {
    try {
      // Reject user via API
      await api.rejectUser(userId);
      
      // Remove from pending users in state
      dispatch({ type: "REJECT_USER", payload: userId });
    } catch (error) {
      console.error('Error rejecting user:', error);
      throw error;
    }
  }, []);

  // Add refresh functions to refetch data from DB
  const refreshBooks = useCallback(async () => {
    try {
      dispatch({ type: "SET_BOOKS_LOADING", payload: true });
      const response = await api.getBooks();
      dispatch({ type: "FETCH_BOOKS_SUCCESS", payload: response.books || [] });
    } catch (error) {
      console.error('Error refreshing books:', error);
      dispatch({ type: "SET_BOOKS_ERROR", payload: true });
    }
  }, []);

  const refreshUsers = useCallback(async () => {
    try {
      dispatch({ type: "SET_USERS_LOADING", payload: true });
      const pendingUsersResponse = await api.getPendingUsers();
      dispatch({ type: "FETCH_USERS_SUCCESS", payload: pendingUsersResponse.users || [] });
    } catch (error) {
      console.error('Error refreshing users:', error);
      dispatch({ type: "SET_USERS_ERROR", payload: true });
    }
  }, []);

  return {
    // State
    user,
    books,
    filteredBooks,
    pendingUsers,
    sortBy,
    isLoading,
    isError,
    isBooksLoading,
    isUsersLoading,
    booksError,
    usersError,
    fuse,
    
    // Setters
    setFilteredBooks,
    setSortBy,
    
    // Actions
    login,
    logout,
    addBook,
    updateBookDownloads,
    approveUser,
    rejectUser,
    
    // Refresh functions
    refreshBooks,
    refreshUsers
  };
};